import Phaser from 'phaser';

import { Subscription } from 'rxjs';
import { ResizeMetrics } from '../../model/resize-metrics';
import { CardView } from '../cards/card-view';
import { CardDeckViewModel } from './card-deck-view-model';
import { TwentyOneMainScene } from '../../scenes/twentyone-main-scene';
import { GameEvent, TwentyOne } from 'game-logic';
import { RoundedLabelView } from '../components/rounded-label-view';
import { CardViewModel } from '../cards/card-view-model';
import { assertNever, delay } from 'src/games/common/helper';
import { Positioning } from '../../model/positioning';
import { UxMetrics } from '../../model/ux-metrics';
// import { DebugContainerVisual } from '../debug/debug-container-visual';

export class CardDeckView extends Phaser.GameObjects.Container {
  private currentResizeMetrics?: ResizeMetrics;
  private subscriptionGroup: Subscription;

  private countLabelView: RoundedLabelView;
  private cardViews: Map<number, CardView> = new Map();
  private destroyTweens: Phaser.Tweens.Tween[] = [];

  constructor(
    scene: TwentyOneMainScene,
    x: number,
    y: number,
    private viewModel: CardDeckViewModel,
  ) {
    super(scene, x, y);

    // new DebugContainerVisual(scene, this);

    this.subscriptionGroup = new Subscription();

    this.countLabelView = new RoundedLabelView(scene, 0, 0, viewModel.countLabelViewModel);
    this.countLabelView.setVisible(false);
    this.add(this.countLabelView);

    this.setName(CardDeckView.name);
    scene.add.existing(this);

    this.subscriptionGroup.add(
      this.viewModel.cardViewModelChanges$.subscribe((change) => {
        switch (change.type) {
          case 'add':
            this.addDeckCard(scene, change.viewModel, change.instant);
            break;
          case 'remove':
            this.removeDeckCard(change.viewModel, change.delay, change.instant);
            break;
          default:
            assertNever(change.type, 'Unknown card deck change type');
        }
      }),
    );
    this.subscriptionGroup.add(
      this.viewModel.onCountLabelChanged$.subscribe((show) => {
        this.countLabelView.setVisible(show);
      }),
    );
    this.subscriptionGroup.add(
      viewModel.logicEventQueue$.subscribe((eventQueue) => {
        eventQueue?.subscribe(this.handleLogicEvents.bind(this));
      }),
    );
  }

  override destroy(fromScene?: boolean | undefined): void {
    this.subscriptionGroup.unsubscribe();

    // Stop any tweens which are destroying child views
    this.destroyTweens.forEach((tween) => {
      tween.stop();
    });

    super.destroy(fromScene);
  }

  public onResize(resizeMetrics: ResizeMetrics) {
    this.countLabelView?.onResize(resizeMetrics);
    this.cardViews.forEach((cardView) => cardView.onResize(resizeMetrics));

    this.currentResizeMetrics = resizeMetrics;
    this.refreshLayout();
  }

  private refreshLayout(): void {
    if (!this.currentResizeMetrics) {
      return;
    }
    const layoutConfig = this.currentResizeMetrics.layoutConfig;
    const scalingMetrics = this.currentResizeMetrics.scalingMetrics;

    const deckRect = Positioning.globalDeckCardRect(layoutConfig, scalingMetrics);

    this.setSize(deckRect.width, deckRect.height);
    this.setPosition(deckRect.pos.x, deckRect.pos.y);

    this.countLabelView.setY((deckRect.height + this.countLabelView.displayHeight) * 0.5);
  }

  private async handleLogicEvents(event: GameEvent<TwentyOne.TwentyOneEvents>): Promise<void> {
    // NOTE: We always process game init events
    switch (event.type) {
      case 'onGameInit':
        {
          const board = event.payload.board;
          for (const [index, card] of board.deck.allCards.entries()) {
            await this.viewModel.addDeckCard(card, index, event.instant);
          }
          if (!event.instant) {
            await delay(UxMetrics.CardDealTravelDuration + UxMetrics.CardDealDuration);
          }
        }
        break;
      case 'onDrawDeckToCurrent':
        {
          this.viewModel.removeCard(event.payload.cardIndex, event.instant);
        }
        break;
      case 'onDrawDeckToNext':
        {
          this.viewModel.removeCard(event.payload.cardIndex, event.instant);
        }
        break;
      case 'onRemainingCardsChanged':
      case 'onDealComplete':
        {
          const remainingCards = event.payload.remainingCards;
          this.viewModel.updateDeckCount(`${remainingCards === 0 ? '' : remainingCards}`);
        }
        break;
      default:
        break;
    }
  }

  private addDeckCard(scene: TwentyOneMainScene, cardViewModel: CardViewModel, instant: boolean) {
    const scaleFactor = this.currentResizeMetrics?.scalingMetrics.scaleFactor ?? 1;
    const boardWidth = this.currentResizeMetrics?.layoutConfig.boardWidth ?? 500;

    // Add the new card view
    const cardView = new CardView(scene, 0, 0, cardViewModel);
    if (this.currentResizeMetrics) {
      cardView.onResize(this.currentResizeMetrics);
    }
    this.add(cardView);

    const cardCount = this.cardViews.size;
    this.cardViews.set(cardView.cardIndex, cardView);

    if (instant) {
      cardView.setPosition(0, 0);
      cardView.setScale(0.75, 0.75);

      if (cardCount > 0) {
        cardViewModel.enableShadow(false);

        //
        // TODO: Why is this here
        cardView.setVisible(false);
      }
      return;
    }

    const cardIndex = cardViewModel.cardIndex / 52;
    const randY = -1 * (cardIndex * 0.5 - 0.25);
    const initialX = boardWidth * 1.25 * scaleFactor;
    const initialY = boardWidth * randY * scaleFactor;

    cardView.setPosition(initialX, initialY);
    cardView.setScale(1.1, 1.1);
    cardView.setAngle(Phaser.Math.Between(-25, 25));

    this.scene.tweens.add({
      targets: [cardView],
      x: 0,
      y: 0,
      scale: 0.75,
      angle: 0,
      duration: UxMetrics.CardDealTravelDuration,
      ease: Phaser.Math.Easing.Sine.In,
      onComplete: () => {
        if (cardCount > 0) {
          cardViewModel.enableShadow(false);
          cardView.setVisible(false);
        }
      },
    });
  }

  private removeDeckCard(cardViewModel: CardViewModel, delayMs: number, instant: boolean) {
    const foundView = this.cardViews.get(cardViewModel.cardIndex);
    if (foundView) {
      if (instant) {
        foundView.destroy();
      } else {
        const disposePt = Positioning.globalWastePosition(
          this.viewModel.layoutConfig,
          this.currentResizeMetrics?.scalingMetrics,
        );
        const localDisposePt = new Phaser.Math.Vector2();
        this.getLocalPoint(disposePt.x, disposePt.y, localDisposePt);

        const destroyTween = this.scene.tweens.add({
          targets: [foundView],
          x: localDisposePt.x,
          y: localDisposePt.y,
          duration: delayMs,
          ease: Phaser.Math.Easing.Quadratic.Out,
          onComplete: () => {
            foundView.destroy();
          },
        });
        this.destroyTweens.push(destroyTween);
      }
    }
    this.cardViews.delete(cardViewModel.cardIndex);
  }
}
